import {
  isoKw,
  MONAT_TAGE,
  montagVon,
  QUARTAL_TAGE,
  tageSpaeter,
  werktagRueckwaerts,
  werktagVorwaerts,
  werktageBis,
  werktageSpaeter,
} from './zeit'

/**
 * **Wann ist eine Aufgabe dran?** – die Ableitung hinter der Zeitachse in der Planung
 * (19.08.2026, [E-147](../../../docs/ENTSCHEIDUNGEN.md)).
 *
 * Nils: *„In die Zeitfenster spielen ja auch Aufgaben rein mit eh einer Fälligkeit oder sodass
 * ich genau sehe welche Aufgaben wann bei welchen Leuten dran sein könnten, was noch weit in
 * der Zukunft liegt, was noch kein Datum hat usw."*
 *
 * ## Warum abgeleitet und nicht gespeichert
 *
 * Es gibt **vier** Angaben, die etwas über „wann" sagen, und sie widersprechen sich nicht,
 * sondern sind verschieden genau:
 *
 * | | |
 * |---|---|
 * | `dueAt` | ein **harter Termin** – jemand hat ein Datum zugesagt |
 * | Meilenstein, den die Aufgabe **bedient** | *„bis dahin muss dieses Stück stehen"* (E-335) |
 * | Zieltermin des Vorhabens | *„bis dahin muss das Vorhaben stehen"* |
 * | Zeitfenster (Aufgabe, sonst Vorhaben) | eine **Dringlichkeitsstufe**, kein Datum |
 * | nichts davon | der Normalfall – **und die Arbeitsliste der Planung** |
 *
 * Eine fünfte Spalte „geplantes Datum" in der Datenbank wäre eine Zahl, die neben den vier
 * anderen steht und bei jeder Änderung nachgezogen werden müsste. Abgeleitet ist sie immer
 * aktuell, und **woher** sie stammt, bleibt sichtbar – das ist der Unterschied zwischen
 * *„zugesagt zum 30.09."* und *„irgendwann dieses Quartal"*.
 *
 * ## Warum ein Zeitfenster überhaupt zu einem Datum wird
 *
 * Weil Nils eine Kalenderfrage stellt und das Zeitfenster eine Stufe ist. „Innerhalb 5 Tage"
 * beantwortet *„wie dringend"*, nicht *„in welcher Woche"*. Ohne die Umrechnung kann eine
 * Zeitachse Zeitfenster gar nicht zeigen – und getragen wird es kaum: **7 von 122** offenen
 * Aufgaben am 19.08.2026, alle von Hand, weil nichts im Zulauf eines erzeugt.
 */

/** Woher das Datum kommt – steht an jeder Aufgabe, weil „zugesagt" mehr wiegt als „geschätzt". */
export const DATUMSQUELLEN = ['faellig', 'meilenstein', 'zieltermin', 'zeitfenster'] as const
export type Datumsquelle = (typeof DATUMSQUELLEN)[number]

export const DATUMSQUELLE_LABELS: Record<Datumsquelle, string> = {
  faellig: 'Fälligkeit',
  meilenstein: 'Meilenstein',
  zieltermin: 'Zieltermin des Vorhabens',
  zeitfenster: 'aus dem Zeitfenster',
}

/**
 * **Ein Zeitfenster als Datum.** Bewusst grob – die Stufe ist grob.
 *
 * ## Alle fünf Stufen sind Fristen, keine Kalendereinträge (Nils, 21.08.2026)
 *
 * *„‚Quartal' heißt bei dir dieses Quartal. Ich meinte damit aber ‚innerhalb von 3 Monaten'.
 * Und Monat heißt immer 30 Tage, Quartal eben 3 Monate. Sonst sind ja manchmal 10 Tage mehr
 * als Monat oder Quartal."*
 *
 * Genau das war der Fehler: `monat` lag auf dem **Monatsende**, `quartal` auf dem
 * **Quartalsende**. Am 28. September gesetzt hieß „Quartal" damit *bis übermorgen* – und
 * „innerhalb 10 Werktagen" reichte zwei Wochen weiter als die beiden Stufen, die darüber
 * stehen sollten. Die Leiter `jetzt < tage5 < tage10 < monat < quartal` galt dann nicht mehr,
 * und zwar ausgerechnet am Ende jedes Zeitraums, wenn Fristen knapp werden.
 *
 * Deshalb zählt jede Stufe ab dem Anker eine feste Länge:
 *
 * | Stufe | Länge |
 * |---|---|
 * | `jetzt` | derselbe Tag |
 * | `tage5` / `tage10` | 5 bzw. 10 **Werktage** |
 * | `monat` | **30 Kalendertage** |
 * | `quartal` | **3 Monate = 90 Kalendertage** |
 *
 * Monat und Quartal in Kalendertagen, nicht in Werktagen: „drei Monate" ist eine Aussage über
 * verstrichene Zeit, keine über Arbeitszeit – und 90 Werktage wären ein Dreivierteljahr.
 *
 * ## Gemessen ab der Einstufung, nicht ab heute (20.08.2026)
 *
 * Hier stand `heute`. Damit wanderte das Datum jeden Tag mit: eine Aufgabe auf „10 Tage" war
 * heute bis in zehn Tagen fällig und morgen wieder – **sie konnte nie ablaufen**. Auf der
 * Zeitachse rutschte sie Tag für Tag nach rechts und landete nie in der Spalte
 * „überfällig".
 *
 * Der zweite Parameter ist deshalb der **Anker**: der Zeitpunkt, an dem jemand die Stufe
 * gesetzt hat (`zeitfenster_ab`). „Innerhalb 30 Tagen", am 3. Juli gesagt, ist am 2. August
 * vorbei, auch wenn man im Oktober darauf sieht.
 *
 * `ab === null` gibt es nur bei Zeilen aus der Zeit vor der Spalte. Dann bleibt es beim alten
 * Verhalten (ab heute), weil die Alternative wäre, ein Fenster gar nicht anzuzeigen – und ein
 * verschwundener Eintrag ist schlechter als ein ungenauer.
 *
 * ## Das Ergebnis ist immer ein Werktag (Nils, 20.08.2026)
 *
 * *„Bei Zeitfenster die Tage – das sind immer Werktage. Monat, Quartal natürlich nicht, dann
 * aber immer der letzte Werktag in dem Monat oder Quartal."*
 *
 * „Innerhalb 10 Tage", am Donnerstag gesetzt, hieß einmal **bis Sonntag** – eine Frist, die an
 * zwei Tagen endet, an denen niemand arbeitet, ist real zwei Tage kürzer als sie dasteht. Die
 * Tage-Fenster rechnen deshalb vorwärts über Werktage; `monat` und `quartal` fallen vom Ende
 * ihrer 30 bzw. 90 Tage auf den letzten Werktag **zurück** – vorwärts hieße, das Fenster zu
 * verlängern, um dessen Länge es gerade geht.
 *
 * Feiertage bleiben außen vor (siehe `zeit.ts`); ein Werktag ist Montag bis Freitag.
 */
export function zeitfensterDatum(
  zeitfenster: string,
  ab: Date | string | null,
  heute: Date = new Date(),
): Date | null {
  const anker = ab === null ? heute : new Date(ab)
  const tag = new Date(anker.getFullYear(), anker.getMonth(), anker.getDate())
  switch (zeitfenster) {
    /** Am Wochenende gesetzt heißt „gleich am Montag" – ein Sonntag als Frist ist keine. */
    case 'jetzt':
      return werktagVorwaerts(tag)
    case 'tage5':
      return werktageSpaeter(tag, 5)
    case 'tage10':
      return werktageSpaeter(tag, 10)
    case 'monat':
      return werktagRueckwaerts(tageSpaeter(tag, MONAT_TAGE))
    case 'quartal':
      return werktagRueckwaerts(tageSpaeter(tag, QUARTAL_TAGE))
    default:
      return null
  }
}

/**
 * **Ist die Frist eines Zeitfensters abgelaufen?**
 *
 * Die Frage, die es vor dem Anker nicht geben konnte. Ohne Stempel (`ab === null`) lautet die
 * Antwort **nein** – nicht, weil die Aufgabe pünktlich wäre, sondern weil niemand es wissen
 * kann. Eine unbekannte Frist als überfällig zu melden hieße, alte Zeilen anzuklagen.
 */
export function zeitfensterAbgelaufen(
  zeitfenster: string | null,
  ab: Date | string | null,
  heute: Date = new Date(),
): boolean {
  if (zeitfenster === null || ab === null) return false
  const ziel = zeitfensterDatum(zeitfenster, ab, heute)
  if (ziel === null) return false
  /** Der Zieltag zählt noch dazu: „bis Freitag" heißt einschließlich Freitag. */
  return new Date(heute.getFullYear(), heute.getMonth(), heute.getDate()) > ziel
}

/**
 * **Das Zeitfenster, wie es dasteht** – Stufe, Frist und ob sie vorbei ist (20.08.2026).
 *
 * Eine Funktion und nicht drei Aufrufe an der Anzeige, weil die drei Angaben zusammen gelesen
 * werden müssen: „10 Tage" ohne Datum ist die Frage, die Nils gestellt hat, und „bis 30.08."
 * ohne die Stufe verliert, wie verbindlich es gemeint war.
 *
 * `ziel === null` heißt **kein Anker** – die Zeile stammt aus der Zeit vor der Spalte. Dann
 * steht nur die Stufe da, ohne erfundenes Datum.
 */
export function zeitfensterStand(
  zeitfenster: string | null,
  ab: Date | string | null,
  heute: Date = new Date(),
): { ziel: Date | null; abgelaufen: boolean; datum: string | null } {
  if (zeitfenster === null || ab === null) {
    return { ziel: null, abgelaufen: false, datum: null }
  }
  const ziel = zeitfensterDatum(zeitfenster, ab, heute)
  if (ziel === null) return { ziel: null, abgelaufen: false, datum: null }
  return {
    ziel,
    abgelaufen: zeitfensterAbgelaufen(zeitfenster, ab, heute),
    datum: new Intl.DateTimeFormat('de-DE', {
      weekday: 'short',
      day: '2-digit',
      month: '2-digit',
    }).format(ziel),
  }
}

export type PlanungsAufgabe = {
  dueAt: Date | string | null
  zeitfenster: string | null
  /** Der Anker des Fensters. `null` bei Zeilen aus der Zeit vor der Spalte. */
  zeitfensterAb?: Date | string | null
  vorhabenId: string | null
  /**
   * **Der Meilenstein, den diese Aufgabe bedient** – und nur der zieht sie an sein Datum
   * (09.09.2026, [E-335](../../../docs/ENTSCHEIDUNGEN.md#e-335)). Bis dahin galt der nächste
   * offene Meilenstein des Vorhabens für **alle** seine Aufgaben; bei zwanzig Aufgaben hieß
   * das zwanzigmal derselbe Termin, gleich ob eine davon etwas mit ihm zu tun hatte.
   */
  meilensteinId: string | null
}

export type PlanungsVorhaben = {
  id: string
  zeitfenster: string | null
  zeitfensterAb?: Date | string | null
  zieltermin: Date | string | null
}

export type PlanungsMeilenstein = {
  id: string
  vorhabenId: string
  /**
   * **Darf fehlen** (E-273): ein Meilenstein steht oft vor seinem Datum fest. Für die Planung
   * ist er dann stumm – er sagt „das kommt noch", aber nicht „wann".
   */
  termin: string | null
  erreichtAm: Date | string | null
}

export type Termin = { datum: Date; quelle: Datumsquelle } | null

/**
 * **Welchen Meilenstein bedient diese Aufgabe?** – derselbe Griff für die Terminableitung und
 * für die Beschriftung („es gilt der Meilenstein «Abnahme»"), damit beide denselben nennen.
 *
 * `undefined`, wenn sie keinen bedient oder der Bezug in ein anderes Vorhaben zeigt.
 */
export function meilensteinDerAufgabe<M extends PlanungsMeilenstein>(
  aufgabe: Pick<PlanungsAufgabe, 'vorhabenId' | 'meilensteinId'>,
  meilensteine: readonly M[],
): M | undefined {
  if (aufgabe.meilensteinId === null || aufgabe.vorhabenId === null) return undefined
  return meilensteine.find(
    (m) => m.id === aufgabe.meilensteinId && m.vorhabenId === aufgabe.vorhabenId,
  )
}

/**
 * **Das wirksame Datum einer Aufgabe** – in dieser Reihenfolge, erste Angabe gewinnt.
 *
 * Die Reihenfolge ist nicht beliebig: sie geht von **zugesagt** über **geplant** zu
 * **eingeschätzt**. Ein Zeitfenster darf einen harten Termin nicht überschreiben, auch wenn es
 * früher liegt – sonst verschwindet die Zusage aus dem Bild, sobald jemand „quartal" setzt.
 */
export function wirksamerTermin(
  aufgabe: PlanungsAufgabe,
  vorhaben: ReadonlyMap<string, PlanungsVorhaben>,
  meilensteine: readonly PlanungsMeilenstein[],
  heute: Date,
): Termin {
  if (aufgabe.dueAt !== null) {
    return { datum: new Date(aufgabe.dueAt), quelle: 'faellig' }
  }

  const v = aufgabe.vorhabenId === null ? undefined : vorhaben.get(aufgabe.vorhabenId)

  if (v !== undefined) {
    /**
     * **Der Meilenstein, den die Aufgabe bedient** – nicht der nächste des Vorhabens
     * ([E-335](../../../docs/ENTSCHEIDUNGEN.md#e-335)). Nils: *„der Meilenstein nimmt zwar
     * eine Verbindungsrolle ein – aber nicht die Steuerung der Fälligkeit."* Wer keinen
     * bedient, fällt auf Zieltermin und Zeitfenster zurück.
     *
     * Nur ein **offener, terminierter** zählt: ein erreichter sagt nichts mehr über „wann",
     * und einer ohne Datum beantwortet die Frage nicht (E-273). Und nur einer **desselben
     * Vorhabens** – ein Bezug, der einen Vorhabenwechsel überlebt hat, ist kein Termin.
     */
    const bedient = meilensteinDerAufgabe(aufgabe, meilensteine)
    if (bedient !== undefined && bedient.erreichtAm === null && bedient.termin !== null) {
      return {
        datum: new Date(`${bedient.termin.slice(0, 10)}T12:00:00`),
        quelle: 'meilenstein',
      }
    }
    if (v.zieltermin !== null) {
      return { datum: new Date(v.zieltermin), quelle: 'zieltermin' }
    }
  }

  /**
   * Das **wirksame** Fenster: das der Aufgabe, sonst das ihres Vorhabens (E-108).
   *
   * **Der Anker kommt von derselben Seite wie die Stufe.** Das Fenster der Aufgabe mit dem
   * Ankerdatum des Vorhabens zu rechnen wäre ein Mischwert aus zwei Entscheidungen, die zu
   * verschiedenen Zeitpunkten gefallen sind – und das Ergebnis stünde in keinem Zusammenhang
   * mit beiden.
   */
  const geerbt = aufgabe.zeitfenster === null
  const fenster = geerbt ? (v?.zeitfenster ?? null) : aufgabe.zeitfenster
  const anker = (geerbt ? (v?.zeitfensterAb ?? null) : (aufgabe.zeitfensterAb ?? null)) ?? null
  if (fenster !== null) {
    const datum = zeitfensterDatum(fenster, anker, heute)
    if (datum !== null) return { datum, quelle: 'zeitfenster' }
  }

  return null
}

/** Die Spalten der Zeitachse. `ueberfaellig` und `ohne` sind keine Wochen – deshalb eigene Werte. */
export type Spalte =
  | { art: 'ueberfaellig' }
  | { art: 'woche'; kw: string; montag: string }
  | { art: 'spaeter' }
  | { art: 'ohne' }

/**
 * **Die Spalten: überfällig · N Wochen · später · ohne Datum.**
 *
 * „Überfällig" steht **vorn und getrennt**, nicht in der laufenden Woche: ein Termin, der
 * vorbei ist, ist eine andere Aussage als einer, der diese Woche fällig wird. In der laufenden
 * Woche versteckt, wäre er nach zwei Tagen unsichtbar.
 *
 * „Ohne Datum" steht **hinten** – es ist die längste Spalte und zugleich die Arbeitsliste:
 * hier liegt, was noch niemand eingeplant hat.
 */
export function spalten(heute: Date, wochen: number): Spalte[] {
  const montag = new Date(`${montagVon(heute)}T12:00:00`)
  const wochenSpalten: Spalte[] = []
  for (let i = 0; i < wochen; i++) {
    const m = new Date(montag.getFullYear(), montag.getMonth(), montag.getDate() + i * 7)
    const schluessel = montagVon(m)
    wochenSpalten.push({ art: 'woche', kw: isoKw(schluessel), montag: schluessel })
  }
  return [{ art: 'ueberfaellig' }, ...wochenSpalten, { art: 'spaeter' }, { art: 'ohne' }]
}

/** In welche Spalte ein Termin fällt. Ohne Termin: `ohne`. */
export function spalteFuer(termin: Termin, heute: Date, wochen: number): Spalte['art'] | string {
  if (termin === null) return 'ohne'

  const dieseWoche = montagVon(heute)
  const datumsMontag = montagVon(termin.datum)
  if (datumsMontag < dieseWoche) return 'ueberfaellig'

  const letzterMontag = new Date(`${dieseWoche}T12:00:00`)
  letzterMontag.setDate(letzterMontag.getDate() + (wochen - 1) * 7)
  if (datumsMontag > montagVon(letzterMontag)) return 'spaeter'

  return isoKw(datumsMontag)
}

/** Der Schlüssel einer Spalte – `kw` bei Wochen, sonst die Art. Damit werden Zellen gefunden. */
export function spaltenSchluessel(s: Spalte): string {
  return s.art === 'woche' ? s.kw : s.art
}

/** Beschriftung für den Spaltenkopf. */
export function spaltenLabel(s: Spalte): string {
  switch (s.art) {
    case 'ueberfaellig':
      return 'überfällig'
    case 'spaeter':
      return 'später'
    case 'ohne':
      return 'ohne Datum'
    case 'woche':
      return `KW ${s.kw.slice(-2)}`
  }
}

/** Liegt diese Woche in der Gegenwart? Der Spaltenkopf hebt sie hervor. */
export function istDieseWoche(s: Spalte, heute: Date): boolean {
  return s.art === 'woche' && s.montag === montagVon(heute)
}

/** Fällt ein Meilenstein in diese Woche? Dann trägt der Kopf ein ◆. */
export function meilensteineInWoche(
  s: Spalte,
  meilensteine: readonly PlanungsMeilenstein[],
): PlanungsMeilenstein[] {
  if (s.art !== 'woche') return []
  return meilensteine.filter((m) => {
    if (m.erreichtAm !== null || m.termin === null) return false
    const tag = m.termin.slice(0, 10)
    return montagVon(new Date(`${tag}T12:00:00`)) === s.montag
  })
}

/** Kurzform `Mo, 24.08.` für den Spaltenkopf – aus dem Tagesschlüssel, nicht aus `Date`. */
export function montagKurz(montag: string): string {
  const [, monat, tag] = montag.split('-')
  return `${tag}.${monat}.`
}

/* ── Dringlichkeit: „wann ist das dran" für die Geplant-Kachel ─────────────── */

/**
 * **Die Stufen der Geplant-Kachel im Leitstand** (Nils, 20.08.2026).
 *
 * Aus der Abnahme von Phase 2: *„das rückblickende Daily kann ich problemlos halten, aber was
 * liegt heute an für wen, was morgen usw. fehlt"* – und auf die Frage, wonach gruppiert werden
 * soll: *„Nach Zeitfenster, Dringlichkeit (Pool Sofort ist immer sofort), Fälligkeit."*
 *
 * ## Warum Stufen und keine Kalendertage
 *
 * Weil eine Tagesspalte am 20.08.2026 **eine Zeile** gehabt hätte. Gemessen
 * (`planung-messen-cli.ts`): von 114 offenen Aufgaben tragen 0 eine Fälligkeit, 0 einen
 * Meilenstein, 0 einen Zieltermin und 10 ein Zeitfenster. Eine Achse „heute · morgen ·
 * übermorgen" wäre eine Ansicht, die auf Daten wartet, die niemand pflegt.
 *
 * Die Stufen sind dagegen das, was tatsächlich gesetzt wird – und `ohne` ist keine Restspalte,
 * sondern die **Arbeitsliste**: sie trägt heute 103 von 114 und sagt damit die Wahrheit über
 * den Planungsstand.
 */
export const DRINGLICHKEITEN = [
  'ueberfaellig',
  'sofort',
  'tage5',
  'tage10',
  'monat',
  'quartal',
  'ohne',
] as const
export type Dringlichkeit = (typeof DRINGLICHKEITEN)[number]

export const DRINGLICHKEIT_LABELS: Record<Dringlichkeit, string> = {
  ueberfaellig: 'überfällig',
  sofort: 'heute',
  tage5: 'in 5 Werktagen',
  tage10: 'in 10 Werktagen',
  monat: 'in 30 Tagen',
  quartal: 'in 3 Monaten',
  ohne: 'ohne Datum',
}

/** Für den Spaltenkopf – die Tabelle hat sieben davon nebeneinander. */
export const DRINGLICHKEIT_KURZ: Record<Dringlichkeit, string> = {
  ueberfaellig: 'überfällig',
  sofort: 'heute',
  tage5: '5 T',
  tage10: '10 T',
  monat: '30 T',
  quartal: '3 Mon',
  ohne: 'ohne',
}

/** Woher die Stufe kommt. `pool` ist keine Datumsquelle – sie hat gar kein Datum. */
export type Dringlichkeitsquelle = Datumsquelle | 'pool'

export const DRINGLICHKEITSQUELLE_LABELS: Record<Dringlichkeitsquelle, string> = {
  ...DATUMSQUELLE_LABELS,
  pool: 'Pool „Sofort"',
}

/** Ganze Tage zwischen zwei Zeitpunkten – über **Tagesgrenzen**, nicht über 24-Stunden-Blöcke. */
function tageBis(ziel: Date, heute: Date): number {
  const a = Date.UTC(heute.getFullYear(), heute.getMonth(), heute.getDate())
  const b = Date.UTC(ziel.getFullYear(), ziel.getMonth(), ziel.getDate())
  return Math.round((b - a) / 86_400_000)
}

/**
 * **Wie dringend ist eine Aufgabe?** – aus dem wirksamen Termin und dem Pool.
 *
 * ## Die Reihenfolge, und warum sie so ist
 *
 * 1. **Überfällig schlägt alles.** Auch den Sofort-Pool: ein gerissener Termin ist eine
 *    verletzte Zusage, der Pool nur eine Einordnung. Wer beides hat, soll das Schlimmere sehen.
 * 2. **Dann der Pool „Sofort".** Nils: *„Pool Sofort ist immer sofort."* Er überschreibt damit
 *    ausdrücklich ein Zeitfenster, das weiter hinten läge – wer eine Aufgabe in den
 *    Sofort-Pool legt, hat die Einordnung schon getroffen, und ein „quartal" daneben ist ein
 *    Rest aus der Zeit davor.
 * 3. **Dann der Termin**, in Tagen gemessen. Woher er stammt, entscheidet `wirksamerTermin`:
 *    Fälligkeit vor Meilenstein vor Zieltermin vor Zeitfenster.
 *
 * ## Warum die Stufe aus dem Datum zurückgerechnet wird
 *
 * Eine Aufgabe auf „10 Tage", vor acht Tagen so eingestuft, ist **in zwei Tagen** fällig und
 * gehört in die Spalte „5 T" – nicht in „10 T". Das ist der ganze Sinn des Ankers
 * (`zeitfenster_ab`, 20.08.2026): ohne ihn stünde sie bis in alle Ewigkeit unter „10 Tage" und
 * könnte nie ablaufen. Die Stufe beschreibt hier **die verbleibende Frist**, nicht die Sorte
 * der Einstufung.
 *
 * `null` beim Termin **und** kein Sofort-Pool heißt `ohne` – und das ist kein Mangel der
 * Rechnung, sondern der Bestand.
 */
export function dringlichkeitVon(
  termin: Termin,
  sofortPool: boolean,
  heute: Date,
): { stufe: Dringlichkeit; quelle: Dringlichkeitsquelle | null } {
  if (termin !== null) {
    const tage = tageBis(termin.datum, heute)
    if (tage < 0) return { stufe: 'ueberfaellig', quelle: termin.quelle }
    if (sofortPool) return { stufe: 'sofort', quelle: 'pool' }
    if (tage === 0) return { stufe: 'sofort', quelle: termin.quelle }
    /**
     * **Die Restfrist in Werktagen** – dieselbe Rechnung wie beim Setzen des Fensters
     * (20.08.2026). Kalendertage hätten hier eine Aufgabe, die *heute* auf „10 Tage" gestellt
     * wurde, in die Spalte „Monat" gelegt: zehn Werktage sind vierzehn Kalendertage, und der
     * Vergleich wäre gegen die Zahl auf dem Knopf gelaufen, nicht gegen deren Bedeutung.
     */
    const werktage = werktageBis(termin.datum, heute)
    if (werktage <= 5) return { stufe: 'tage5', quelle: termin.quelle }
    if (werktage <= 10) return { stufe: 'tage10', quelle: termin.quelle }
    /**
     * **30 und 90 Kalendertage, nicht Monats- und Quartalsende** (21.08.2026). Hier stand die
     * Kalenderaussage – und damit fiel am 28. eines Monats alles ab dem 1. in die Spalte
     * „Quartal", obwohl es zehn Tage entfernt lag. Dieselbe Länge wie beim Setzen des
     * Fensters (`zeitfensterDatum`), sonst zeigt die Kachel eine andere Stufe an als der Knopf.
     *
     * `quartal` ist zugleich die **Sammelspalte**: was weiter als 90 Tage draußen liegt, hat
     * keine eigene Stufe – hinter dem Quartal endet die Leiter (Kap. 4.5).
     */
    if (tage <= MONAT_TAGE) return { stufe: 'monat', quelle: termin.quelle }
    return { stufe: 'quartal', quelle: termin.quelle }
  }

  if (sofortPool) return { stufe: 'sofort', quelle: 'pool' }
  return { stufe: 'ohne', quelle: null }
}

/**
 * **Wie eine Frist an einer Listenzeile steht** (31.08.2026,
 * [E-281](../../../docs/ENTSCHEIDUNGEN.md#e-281)).
 *
 * Nils, mit einem Bild von „Mein Tag": *„Wenn etwas Überfällig ist, dann müssen wir das denke
 * ich noch besser kennzeichnen (Siehe hier, RTM ist eigentlich 28.8. fällig gewesen, heute haben
 * wir den 31.)."*
 *
 * ## Was da schiefging – und warum es kein Anzeigefehler war
 *
 * An der Zeile stand **„ohne Frist"**. Die Ursache: „Mein Tag" las nur `zeitfenster`, und die
 * Aufgabe hatte keins – ihr Termin stand in `due_at`. Dieselbe Verengung steckte in „Klopft
 * an": die Art `frist` hing an `zeitfensterAbgelaufen`, also **meldete eine gerissene
 * Fälligkeit überhaupt nichts**. Ein Datum, das ein Mensch ausdrücklich gesetzt hat, war damit
 * die einzige Terminart, die keine Wirkung hatte.
 *
 * Diese Funktion nimmt deshalb den **wirksamen** Termin entgegen (`wirksamerTermin`) und nicht
 * das Zeitfenster – dieselbe Reihenfolge wie überall sonst
 * ([E-147](../../../docs/ENTSCHEIDUNGEN.md#e-147)).
 *
 * ## Warum eine eigene Funktion neben `dringlichkeitVon`
 *
 * Weil die beiden verschiedene Fragen beantworten. `dringlichkeitVon` sortiert **in eine von
 * sieben Spalten** und braucht dafür Werktage und den Sofort-Pool. Hier geht es um **einen
 * Satz an einer Zeile**, und der zählt Kalendertage: „3 Tage überfällig" ist eine Aussage über
 * den Kalender, nicht über Arbeitstage. Beide auf denselben Termin zu setzen, hält sie
 * beieinander; sie zusammenzulegen hieße, eine Spaltenlogik in einen Satz zu pressen.
 *
 * `null` heißt **kein Termin**. Der Aufrufer schreibt dann „ohne Frist" – aber eben nur dann,
 * und das ist der ganze Unterschied.
 */
export type Fristmarke = {
  text: string
  /** Für die Farbe: `ueberfaellig` rot, `heute` und `bald` in Warnfarbe, `offen` blass. */
  stufe: 'ueberfaellig' | 'heute' | 'bald' | 'offen'
}

export function fristMarke(termin: Termin, heute: Date): Fristmarke | null {
  if (termin === null) return null

  const tage = tageBis(termin.datum, heute)

  /**
   * **Überfällig zählt aufwärts, nicht abwärts.** „seit dem 28.08." verlangt einen Blick in den
   * Kalender, „3 Tage überfällig" nicht – und die Zahl ist genau das, was zum Handeln bewegt.
   */
  if (tage < 0) {
    return {
      text: tage === -1 ? '1 Tag überfällig' : `${-tage} Tage überfällig`,
      stufe: 'ueberfaellig',
    }
  }
  if (tage === 0) return { text: 'heute fällig', stufe: 'heute' }
  if (tage === 1) return { text: 'morgen fällig', stufe: 'bald' }
  if (tage <= 7) return { text: `in ${tage} Tagen`, stufe: 'bald' }

  /**
   * Ab einer Woche das **Datum**: „in 23 Tagen" löst nichts aus und ist beim Überfliegen einer
   * Liste eine Zahl, die man gegen nichts halten kann.
   */
  return {
    text: `bis ${new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit' }).format(
      termin.datum,
    )}`,
    stufe: 'offen',
  }
}
